import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, Search, MoreVertical, Play, Pause, Trash2, Users } from 'lucide-react'
import { useCampaigns, useDeleteCampaign, useStartCampaign, usePauseCampaign, useResumeCampaign } from '@hooks/useCampaigns'
import { Campaign } from '@/types'
import { formatDateTime, formatNumber } from '@lib/utils'
import { CampaignFilters } from '@services/campaign.service'

export function CampaignList() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<CampaignFilters>({
    page: 1,
    limit: 10,
  })
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isLoading, error } = useCampaigns(filters)
  const deleteMutation = useDeleteCampaign()
  const startMutation = useStartCampaign()
  const pauseMutation = usePauseCampaign()
  const resumeMutation = useResumeCampaign()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // Only set search if non-empty, otherwise remove it
    const newFilters = { ...filters, page: 1 }
    if (searchTerm.trim()) {
      newFilters.search = searchTerm.trim()
    } else {
      delete newFilters.search
    }
    setFilters(newFilters)
  }

  const handleStatusFilter = (status: Campaign['status'] | undefined) => {
    setFilters({ ...filters, status, page: 1 })
  }

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this campaign?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-6 text-center">
        <p className="text-rose-400">Failed to load campaigns</p>
      </div>
    )
  }

  const campaigns = data?.data || []
  const pagination = data?.pagination

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <form onSubmit={handleSearch} className="flex flex-1 gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search campaigns..."
              className="input-modern pl-10"
            />
          </div>
          <button
            type="submit"
            className="btn-primary"
          >
            Search
          </button>
        </form>

        <div className="flex gap-2">
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              !filters.status
                ? 'bg-cyan-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleStatusFilter('ACTIVE')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filters.status === 'ACTIVE'
                ? 'bg-emerald-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => handleStatusFilter('DRAFT')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filters.status === 'DRAFT'
                ? 'bg-slate-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Draft
          </button>
        </div>
      </div>

      {/* Campaign Table */}
      {campaigns.length === 0 ? (
        <div className="flex flex-col items-center justify-center stat-card p-12">
          <Mail className="mb-4 h-16 w-16 text-slate-600" />
          <h3 className="text-lg font-semibold text-slate-200">No campaigns found</h3>
          <p className="mt-2 text-sm text-slate-500">
            {filters.search || filters.status
              ? 'Try adjusting your filters'
              : 'Create your first campaign to get started'}
          </p>
        </div>
      ) : (
        <div className="stat-card p-0">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50 border-b border-slate-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Clients
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Stats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {campaigns.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  onView={() => navigate(`/campaigns/${campaign.id}`)}
                  onStart={() => startMutation.mutate(campaign.id)}
                  onPause={() => pauseMutation.mutate(campaign.id)}
                  onResume={() => resumeMutation.mutate(campaign.id)}
                  onDelete={() => handleDelete(campaign.id)}
                />
              ))}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-700/50 px-6 py-4">
              <p className="text-sm text-slate-400">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                  disabled={pagination.page === pagination.totalPages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface CampaignRowProps {
  campaign: Campaign
  onView: () => void
  onStart: () => void
  onPause: () => void
  onResume: () => void
  onDelete: () => void
}

function CampaignRow({ campaign, onView, onStart, onPause, onResume, onDelete }: CampaignRowProps) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Extract stats with defaults to handle undefined values
  const totalClients = campaign.totalClients ?? 0
  const emailsSent = campaign.emailsSent ?? 0
  const emailsDelivered = campaign.emailsDelivered ?? 0
  const opensCount = campaign.opensCount ?? 0
  const clicksCount = campaign.clicksCount ?? 0

  const progress = totalClients > 0
    ? (emailsSent / totalClients) * 100
    : 0

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  return (
    <tr className="hover:bg-slate-800/30 cursor-pointer transition-colors" onClick={onView}>
      <td className="px-6 py-4">
        <div className="flex items-center">
          <Mail className="mr-3 h-5 w-5 text-slate-500" />
          <div>
            <div className="font-medium text-slate-100">{campaign.name}</div>
            <div className="text-sm text-slate-500">ID: {campaign.id}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <StatusBadge status={campaign.status} sendingStatus={campaign.sendingStatus} />
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-slate-500" />
          <span className="font-medium text-slate-200">{formatNumber(totalClients)}</span>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="w-32">
          <div className="mb-1 flex justify-between text-xs text-slate-400">
            <span>{emailsSent}</span>
            <span>{totalClients}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-700">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-indigo-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-slate-200">
          <div>Delivered: {formatNumber(emailsDelivered)}</div>
          <div className="text-slate-500">
            Opens: {formatNumber(opensCount)} | Clicks: {formatNumber(clicksCount)}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-slate-500">
        {formatDateTime(campaign.createdAt)}
      </td>
      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
          >
            <MoreVertical size={18} />
          </button>
          {showMenu && (
            <div className="absolute right-0 z-50 mt-2 w-48 rounded-xl bg-slate-800 border border-slate-700 shadow-xl shadow-black/20 overflow-hidden">
              {campaign.status === 'DRAFT' && (
                <button
                  onClick={() => { onStart(); setShowMenu(false) }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
                >
                  <Play size={16} className="text-emerald-400" /> Start Campaign
                </button>
              )}
              {campaign.status === 'ACTIVE' && (
                <button
                  onClick={() => { onPause(); setShowMenu(false) }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
                >
                  <Pause size={16} className="text-amber-400" /> Pause Campaign
                </button>
              )}
              {campaign.status === 'PAUSED' && (
                <button
                  onClick={() => { onResume(); setShowMenu(false) }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
                >
                  <Play size={16} className="text-emerald-400" /> Resume Campaign
                </button>
              )}
              <div className="border-t border-slate-700">
                <button
                  onClick={() => { onDelete(); setShowMenu(false) }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-rose-400 hover:bg-rose-500/10 transition-colors"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

function StatusBadge({ status, sendingStatus }: { status: Campaign['status'], sendingStatus: Campaign['sendingStatus'] }) {
  const statusColors: Record<string, string> = {
    DRAFT: 'bg-slate-500/15 text-slate-400 border border-slate-500/20',
    ACTIVE: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
    PAUSED: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
    COMPLETED: 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20',
    ARCHIVED: 'bg-slate-500/15 text-slate-500 border border-slate-500/20',
  }

  return (
    <div className="space-y-1">
      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusColors[status] || 'bg-slate-500/15 text-slate-400'}`}>
        {status}
      </span>
      {sendingStatus !== 'idle' && (
        <div className="text-xs text-slate-500">{sendingStatus}</div>
      )}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-4 stat-card">
          <div className="h-12 w-12 skeleton rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 skeleton rounded" />
            <div className="h-3 w-1/4 skeleton rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}
